import { useMemo } from "react";
import { NumberFormatValues } from "react-number-format";

/**
 *  Return placeholder and isAllowed for NumberFormat
 *  digits -- the number of preDecimal digits in a number
 *  decimalPlaces -- the number of decimal digits in a number
 * isAllowed is used by NumberFormat to validate an input value.
 * Invalid value will be rejected, i.e., the key press by a user is ignored.
 * The function validates an input by the following rules
 *  - if digits is undefined, any number of preDecimal digits are allowed
 *  - if decimalPlaces is undefined, any number of decimal digits are allowed
 * -  if decimalPlaces is 0, only whole number is allowed
 *  - placeholder is undefined when digits is undefined
 *  - doesn't support negative number yet
 * The function doesn't check if a value is a number, since NumberFormat will block any non-numeric input.
 *
 */
export const useNumberField = ({
  digits,
  decimalPlaces,
}: {
  digits?: number;
  decimalPlaces?: number;
}) => {
  return useMemo(() => {
    const digitMark = "_";
    const separator = ".";
    if (digits !== undefined && digits <= 0) {
      throw new Error("Digits must be greater than 0");
    }
    if (decimalPlaces !== undefined && decimalPlaces < 0) {
      throw new Error("Decimal places must be greater or equal 0");
    }
    const placeholder = digits ? digitMark.repeat(digits) : undefined;
    const isAllowed = (values: NumberFormatValues) => {
      if (digits === undefined && decimalPlaces === undefined) {
        return true;
      }
      const { value } = values;
      const parts = value.split(separator);
      // check preDecimal digits
      if (digits !== undefined && parts[0].length > digits) {
        return false;
      }
      // check decimal digits
      if (decimalPlaces === undefined) {
        return true;
      }
      // no decimal point is allowed
      if (decimalPlaces === 0 && parts[1] !== undefined) {
        return false;
      }
      // right length must be less or equal decimalPlaces
      if (parts[1]?.length > decimalPlaces) {
        return false;
      }
      return true;
    };
    return {
      placeholder,
      isAllowed: isAllowed,
    };
  }, [digits, decimalPlaces]);
};
